import React from "react";

import cloneDeep from "lodash/cloneDeep";

import Modal from "@cloudscape-design/components/modal";
import Input from "@cloudscape-design/components/input";
import Button from "@cloudscape-design/components/button";
import Box from "@cloudscape-design/components/box";
import Select from "@cloudscape-design/components/select";
import Multiselect from "@cloudscape-design/components/multiselect";
import SpaceBetween from "@cloudscape-design/components/space-between";
import Spinner from "@cloudscape-design/components/spinner";

/* Recoil */
import { useRecoilValue, useSetRecoilState } from "recoil";
import { configStore } from "../stores/config";
import { gameStore } from "../stores/games";
import { gamesAddModalStore } from "../stores/gamesAddModal";
import { gamesTagsStore } from "../stores/gamesTagsModal";
/* ------- */

const BADGE_POSITION_OPTIONS = [
	{ label: "Top left", value: "top_left" },
	{ label: "Top right", value: "top_right" },
	{ label: "Bottom left", value: "bottom_left" },
	{ label: "Bottom right", value: "bottom_right" },
];
const SYSTEM_RTP_BADGE_VALUE = "__system_rtp_up__";

class GamesAddModal extends React.Component {
	componentDidMount() {
		this.loadGamesSection();
		this.loadBadges();
		this.props.setGameTags((prev) => ({
			...prev,
			filters: {
				...prev.filters,
				sorting: {
					column: "created_at",
					order: "desc",
				},
				search: "",
				page: 1,
				limit: 100,
			},
		}));
		setTimeout(() => {
			this.props.Interface.loadGameTags();
		}, 50);
	}

	loadGamesSection() {
		this.props.Interface.request(
			"get",
			{
				class: "data",
				module: "gamesSection",
				method: "list",
			},
			{ name: "test" },
		)
			.then((list) => {
				const options = list.map((section) => ({
					value: String(section.id),
					label: section.name,
				}));
				this.props.setGamesAddModal((prev) => ({
					...prev,
					currentOption: { ...prev.currentOption, section: options },
				}));
			})
			.catch((error) => {
				console.log(error);
			});
	}

	loadBadges() {
		this.props.Interface.request(
			"get",
			{
				class: "data",
				module: "badges",
				method: "list",
			},
			{ name: "test" },
		)
			.then((list) => {
				const customOptions = (list || [])
					.filter((badge) => String(badge?.name || "").toLowerCase() !== "rtpup")
					.map((badge) => ({
						value: String(badge.id),
						label: badge.name,
						status: String(badge.status || "").toUpperCase(),
						textColor: badge.text_color,
						backgroundColor: badge.background_color,
					}));
				const options = [
					{
						value: SYSTEM_RTP_BADGE_VALUE,
						label: "RTP Up",
						status: "ACTIVE",
						systemType: "rtpUp",
					},
					...customOptions,
				];
				this.props.setGamesAddModal((prev) => ({
					...prev,
					currentOption: { ...prev.currentOption, badges: options },
				}));
			})
			.catch((error) => {
				console.log(error);
			});
	}

	loadProviderParams() {
		this.props.setGamesAddModal({ ...this.props.gamesAddModal, loaded: false });

		this.props.Interface.request("get", { class: "data", module: "games", method: "params" }, { providerID: this.props.gamesAddModal.providerID })
			.then((params) => {
				this.props.setGamesAddModal({ ...this.props.gamesAddModal, loaded: true, params, newPoster: false, newBigPoster: false });
			})
			.catch((e) => {
				this.props.setGamesAddModal({ ...this.props.gamesAddModal, loaded: true });
			});
	}

	addGame() {
		this.props.setGamesAddModal({ ...this.props.gamesAddModal, loaded: false });

		let params = {};
		Object.keys(this.props.gamesAddModal).forEach((key) => {
			if (key === "categoryID") {
				params[key] = [];
				this.props.gamesAddModal[key].forEach((category) => {
					params[key].push(category.type);
				});
			} else if (key === "section") {
				params.sectionIDs = Array.isArray(this.props.gamesAddModal.section)
					? this.props.gamesAddModal.section
					: [];
			} else if (key === "rtp" || key === "volatility") params[key] = parseInt(this.props.gamesAddModal[key]);
			else if (key === "badgeAssignments") {
				params.badges = (this.props.gamesAddModal.badgeAssignments || [])
					.map((item) => {
						const isSystemRtp = item?.badgeId === SYSTEM_RTP_BADGE_VALUE || item?.systemType === "rtpUp";
						const parsedRtpValue = parseInt(item?.rtpValue, 10);
						if (isSystemRtp) {
							return {
								systemType: "rtpUp",
								position: item.position,
								rtpValue: Number.isFinite(parsedRtpValue) && parsedRtpValue > 0 ? parsedRtpValue : null,
							};
						}
						return {
							badgeId: parseInt(item.badgeId, 10),
							position: item.position,
						};
					})
					.filter((item) => (item.systemType === "rtpUp" ? item.position : !isNaN(item.badgeId) && item.badgeId > 0 && item.position));
			}
			else if (key === "tags") {
				params.tags = this.props.gamesAddModal.tags.map((tag) => tag.id);
			} else if (key === "metaTags") {
				const metaTagIds = this.props.gamesAddModal.tags.map((tag) => tag.metaTagId);
				params.metaTags = [...new Set(metaTagIds)];
			} else if (key !== "show" && key !== "loaded" && key !== "currentOption") params[key] = this.props.gamesAddModal[key];
		});

		console.log(params);
		const hasDuplicatePositions = new Set((params.badges || []).map((item) => item.position)).size !== (params.badges || []).length;
		if (hasDuplicatePositions) {
			this.props.Interface.addNoty("Error", "Badge positions must be unique");
			this.props.setGamesAddModal({ ...this.props.gamesAddModal, loaded: true });
			return;
		}
		const hasInvalidRtpBadge = (params.badges || []).some((item) => item.systemType === "rtpUp" && (!Number.isFinite(parseInt(item.rtpValue, 10)) || parseInt(item.rtpValue, 10) <= 0));
		if (hasInvalidRtpBadge) {
			this.props.Interface.addNoty("Error", "RTP Up badge requires RTP value");
			this.props.setGamesAddModal({ ...this.props.gamesAddModal, loaded: true });
			return;
		}

		this.props.Interface.request("get", { class: "data", module: "games", method: "add" }, { ...params })
			.then(() => {
				this.props.setGamesAddModal({ ...this.props.gamesAddModal, loaded: true });
				setTimeout(() => {
					this.props.setGamesAddModal({ ...this.props.gamesAddModal, show: false });
				}, 0);

				setTimeout(() => {
					this.props.Interface.loadCasinoData();
				}, 500);
			})
			.catch((e) => {
				this.props.setGamesAddModal({ ...this.props.gamesAddModal, loaded: true });
				console.log(e);
			});
	}

	getBase64(file, key) {
		let reader = new FileReader();
		reader.readAsDataURL(file);
		reader.onload = () => {
			this.updateParam(key, reader.result);
		};
	}

	updateParam(key, value) {
		let params = cloneDeep(this.props.gamesAddModal);
		params[key] = value;
		this.props.setGamesAddModal({ ...params });
	}

	render() {
		const badgeAssignments = (this.props.gamesAddModal.badgeAssignments || []).filter((assignment) => assignment && assignment.position);
		const selectedBadgeByPosition = new Map();
		const rtpValueByPosition = new Map();
		const isDarkMode = typeof document !== "undefined" && document.body.classList.contains("awsui-dark-mode");
		badgeAssignments.forEach((assignment) => {
			const isSystemRtp = assignment?.badgeId === SYSTEM_RTP_BADGE_VALUE || assignment?.systemType === "rtpUp";
			if (isSystemRtp) {
				selectedBadgeByPosition.set(assignment.position, SYSTEM_RTP_BADGE_VALUE);
				rtpValueByPosition.set(assignment.position, assignment?.rtpValue || "");
				return;
			}
			const badgeId = parseInt(assignment.badgeId, 10);
			if (!isNaN(badgeId) && badgeId > 0) selectedBadgeByPosition.set(assignment.position, String(badgeId));
		});

		return (
			<>
				<Modal
					visible={this.props.gamesAddModal.show}
					onDismiss={() => {
						this.props.setGamesAddModal({ ...this.props.gamesAddModal, show: false });
					}}
					header="Add game"
					children={
						<>
							<SpaceBetween direction="vertical" size="m">
								<Select
									filteringType="auto"
									disabled={!this.props.game.loaded}
									placeholder="Provider"
									selectedOption={this.props.game.currentOption.provider}
									onChange={(option) => {
										setTimeout(() => {
											this.loadProviderParams();
										}, 0);
										this.props.setGamesAddModal({ ...this.props.gamesAddModal, providerID: option.detail.selectedOption.type });
										option.detail.selectedOption.type !== this.props.game.currentProvider && this.props.setGame({ ...this.props.game, currentProvider: option.detail.selectedOption.type, currentOption: { ...this.props.game.currentOption, provider: option.detail.selectedOption } });
									}}
									options={this.props.config.game.options.provider}
								/>
								<Multiselect
									filteringType="auto"
									selectedOptions={this.props.gamesAddModal.categoryID}
									onChange={(select) => {
										this.props.setGamesAddModal({ ...this.props.gamesAddModal, categoryID: select.detail.selectedOptions });
									}}
									disabled={!this.props.game.loaded}
									placeholder="Category"
									options={this.props.config.game.options.category.slice(1)}
								/>

								{this.props.gamesAddModal.loaded && this.props.gamesAddModal.params.length ? (
									<>
										{this.props.gamesAddModal.params
											.filter((item) => {
												return item !== "bigPoster" && item !== "poster" && item !== "section";
											})
											.map((item) => (
												<>
													<Box variant="span">{item}: </Box>
													<Input
														inputMode={item === "rtp" || item === "volatility" ? "number" : "text"}
														type={item === "rtp" || item === "volatility" ? "number" : "text"}
														disableBrowserAutocorrect={true}
														placeholder={item}
														value={this.props.gamesAddModal[item]}
														onChange={(input) => {
															this.updateParam(item, input.detail.value);
														}}
													/>
												</>
											))}

										<Box variant="span">poster:</Box>
										<input
											type="file"
											onChange={(ev) => {
												this.getBase64(ev.target.files[0], "newPoster");
											}}></input>
										<Box variant="span">big poster:</Box>
										<input
											type="file"
											onChange={(ev) => {
												this.getBase64(ev.target.files[0], "newBigPoster");
											}}></input>
										<SpaceBetween direction="vertical" size="m">
											<Box variant="span">Section:</Box>
											<Multiselect
												filteringType="auto"
												disabled={!this.props.game.loaded}
												placeholder="Section"
												selectedOptions={this.props.gamesAddModal.currentOption.section.filter((option) =>
													(this.props.gamesAddModal.section || []).includes(parseInt(option.value))
												)}
												onChange={(option) => {
													this.props.setGamesAddModal({
														...this.props.gamesAddModal,
														section: option.detail.selectedOptions
															.map((selected) => parseInt(selected.value))
															.filter((value) => !isNaN(value)),
													});
												}}
												options={this.props.gamesAddModal.currentOption.section}
											/>
										</SpaceBetween>
										<SpaceBetween direction="vertical" size="m">
											<Box variant="span">Badges:</Box>
											<div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
												{BADGE_POSITION_OPTIONS.map((position) => {
													const selectedBadgeId = selectedBadgeByPosition.get(position.value);
													const selectedBadge = this.props.gamesAddModal.currentOption.badges.find((item) => String(item.value) === String(selectedBadgeId));
													const selectedIsRtpBadge = String(selectedBadgeId) === SYSTEM_RTP_BADGE_VALUE;
													const borderColor = selectedBadge ? (selectedBadge.status === "INACTIVE" ? "#d13212" : "#037f0c") : isDarkMode ? "#879596" : "#d5dbdb";
													const badgeOptions = [
														{ value: "", label: "Empty" },
														...this.props.gamesAddModal.currentOption.badges.map((item) => {
															const itemId = String(item.value);
															const usedInAnotherPosition = [...selectedBadgeByPosition.entries()].some(
																([positionValue, badgeId]) => positionValue !== position.value && String(badgeId) === itemId
															);
															return {
																...item,
																label: `${item.label}${item.status === "INACTIVE" ? " (inactive)" : ""}`,
																disabled: usedInAnotherPosition,
															};
														}),
													];
													return (
													<div key={position.value} style={{ border: `2px solid ${borderColor}`, borderRadius: 6, padding: 8, minHeight: 80 }}>
														<div>{position.label}</div>
														<div style={selectedIsRtpBadge ? { display: "grid", gridTemplateColumns: "1fr 150px", gap: 8, alignItems: "center" } : undefined}>
															<Select
																placeholder="Select badge"
																selectedOption={badgeOptions.find((item) => String(item.value) === String(selectedBadgeId)) || badgeOptions[0]}
																options={badgeOptions}
																onChange={(event) => {
																	const nextBadgeId = event.detail.selectedOption?.value;
																	const nextByPosition = new Map(selectedBadgeByPosition);
																	if (!nextBadgeId) nextByPosition.delete(position.value);
																	else nextByPosition.set(position.value, nextBadgeId);
																	const nextAssignments = BADGE_POSITION_OPTIONS
																		.map((item) => {
																			const badgeId = nextByPosition.get(item.value);
																			if (!badgeId) return null;
																			if (String(badgeId) === SYSTEM_RTP_BADGE_VALUE) {
																				return {
																					badgeId: SYSTEM_RTP_BADGE_VALUE,
																					systemType: "rtpUp",
																					position: item.value,
																					rtpValue: rtpValueByPosition.get(item.value) || "",
																				};
																			}
																			const parsedBadgeId = parseInt(badgeId, 10);
																			return !isNaN(parsedBadgeId) && parsedBadgeId > 0 ? { badgeId: parsedBadgeId, position: item.value } : null;
																		})
																		.filter(Boolean);
																	this.props.setGamesAddModal({
																		...this.props.gamesAddModal,
																		badges: nextAssignments.map((item) => item.badgeId),
																		badgeAssignments: nextAssignments,
																	});
																}}
															/>
															{selectedIsRtpBadge ? (
																<Input
																	type="number"
																	inputMode="number"
																	placeholder="RTP value"
																	value={String(rtpValueByPosition.get(position.value) || "")}
																	onChange={(event) => {
																		const nextValue = event.detail.value;
																		const nextAssignments = badgeAssignments.map((assignment) => {
																			if (assignment.position !== position.value) return assignment;
																			if (assignment.badgeId === SYSTEM_RTP_BADGE_VALUE || assignment.systemType === "rtpUp") return { ...assignment, badgeId: SYSTEM_RTP_BADGE_VALUE, systemType: "rtpUp", rtpValue: nextValue };
																			return assignment;
																		});
																		this.props.setGamesAddModal({
																			...this.props.gamesAddModal,
																			badgeAssignments: nextAssignments,
																		});
																	}}
																/>
															) : null}
														</div>
														<div style={{ marginTop: 6, minHeight: 24, color: selectedBadge ? (selectedBadge.status === "INACTIVE" ? "#d13212" : "#037f0c") : "transparent" }}>
															{selectedBadge ? selectedBadge.status : "STATUS"}
														</div>
													</div>
												);
												})}
											</div>
										</SpaceBetween>

										<SpaceBetween direction="horizontal" size="m">
										</SpaceBetween>

										<SpaceBetween direction="vertical" size="s">
											<Box variant="span">Tags:</Box>
											<Multiselect
												filteringType={this.props.gameTags.filters.search === "" && this.props.gameTags.totalPages === 1 ? "auto" : "manual"}
												filteringPlaceholder="Search tags"
												placeholder="Select tags"
												loadingText="Loading tags..."
												empty="No tags"
												statusType={!this.props.gameTags.loaded && "loading"}
												selectedOptions={this.props.gamesAddModal.tags?.map((tag) => ({ label: `#${tag.id} ${tag.name} (meta Id: ${tag.metaTagId})`, value: String(tag.id) })) || []}
												onChange={({ detail }) => {
													this.props.setGamesAddModal((prev) => ({ ...prev, tags: this.props.gameTags.data.filter((tag) => detail.selectedOptions.map((o) => o.value).indexOf(String(tag.id)) > -1) }));
												}}
												options={this.props.gameTags.data.map((tag) => {
													return { value: String(tag.id), label: `#${tag.id} ${tag.name} (meta Id: ${tag.metaTagId})` };
												})}
												onLoadItems={({ detail }) => {
													if (this.props.gameTags.filters.search === "" && this.props.gameTags.totalPages === 1) return;
													if (typeof detail.filteringText !== "undefined") {
														this.props.setGameTags((prev) => ({
															...prev,
															filters: {
																...prev.filters,
																search: detail.filteringText,
															},
														}));
														if (this.searchDebounce) clearTimeout(this.searchDebounce);
														this.searchDebounce = setTimeout(() => {
															this.props.setGameTags((prev) => ({
																...prev,
																filters: {
																	...prev.filters,
																	page: 1,
																},
															}));
															setTimeout(() => {
																this.props.Interface.loadGameTags();
															}, 50);
														}, 300);
													}
												}}
											/>
										</SpaceBetween>
									</>
								) : !this.props.gamesAddModal.loaded ? (
									<Box variant="div" textAlign="center">
										<Spinner size="large" />
									</Box>
								) : (
									<></>
								)}
							</SpaceBetween>
						</>
					}
					footer={
						<>
							<Box
								float="right"
								children={
									<>
										<Box
											variant="span"
											margin={{ right: "s" }}
											children={
												<Button
													variant="normal"
													onClick={() => {
														this.props.setGamesAddModal({ ...this.props.gamesAddModal, show: false });
													}}
													children="Close"
												/>
											}
										/>
										<Box
											variant="span"
											children={
												<Button
													variant="primary"
													onClick={() => {
														this.addGame();
													}}
													children="Ok"
												/>
											}
										/>
									</>
								}
							/>
						</>
					}
				/>
			</>
		);
	}
}

function withRecoil(Component) {
	return function WrappedComponent(props) {
		const gamesAddModal = useRecoilValue(gamesAddModalStore);
		const setGamesAddModal = useSetRecoilState(gamesAddModalStore);
		const game = useRecoilValue(gameStore);
		const setGame = useSetRecoilState(gameStore);
		const config = useRecoilValue(configStore);
		const gameTags = useRecoilValue(gamesTagsStore);
		const setGameTags = useSetRecoilState(gamesTagsStore);

		return <Component {...props} gamesAddModal={gamesAddModal} setGamesAddModal={setGamesAddModal} game={game} setGame={setGame} gameTags={gameTags} setGameTags={setGameTags} config={config} />;
	};
}

export default withRecoil(GamesAddModal);
